"use client";

import type { StudyExercise } from "@/lib/whiteboard/studySpec";
import React, { useState } from "react";
import { StudyText, studyPrimary, studySecondary } from "./StudyCard";
import type { WhiteboardCardEventHandler } from "./types";

// Preserve accents: a conjugation exercise must not silently accept a different form.
const answerKey = (value: string) =>
  value.normalize("NFC").trim().toLocaleLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, " ");
type Practice = Exclude<StudyExercise, { family: "reading" }>;

export function StudyPractice({
  exercise,
  tiles = [],
  focus,
  onEvent,
}: {
  exercise: Practice;
  tiles?: string[];
  focus: string[];
  onEvent?: WhiteboardCardEventHandler;
}) {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [order, setOrder] = useState<number[]>([]);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);
  const expected =
    exercise.family === "conjugation"
      ? exercise.rows.flatMap((row, index) =>
          row.blank ? [{ index, label: row.subject, answer: row.form }] : []
        )
      : exercise.family === "gap_fill"
        ? exercise.items.map((item, index) => ({
            index,
            label: item.sentence,
            answer: item.answer,
          }))
        : [{ index: 0, label: exercise.prompt, answer: exercise.tiles.join(" ") }];
  const value = (index: number) =>
    exercise.family === "word_tiles" ? order.map((i) => tiles[i]).join(" ") : answers[index] || "";
  const canCheck =
    !done &&
    expected.every((item) => value(item.index).trim()) &&
    (exercise.family !== "word_tiles" || order.length === tiles.length);
  const choose = (index: number, next: string) => {
    setAnswers((prev) => ({ ...prev, [index]: next }));
    setChecked((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setStatus("");
  };
  const check = () => {
    if (!canCheck) return;
    const results = Object.fromEntries(
      expected.map((item) => [item.index, answerKey(value(item.index)) === answerKey(item.answer)])
    );
    const count = Object.values(results).filter(Boolean).length;
    const solved = count === expected.length;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setChecked(results);
    setDone(solved);
    setStatus(
      solved
        ? "Correct. Now try using it aloud."
        : `${count} of ${expected.length} correct. Review the marked answers and try again.`
    );
    onEvent?.({
      event: solved ? "completed" : "answered",
      action: "checked_study_exercise",
      correct: solved,
      response: expected.map((item) => `${item.label}: ${value(item.index)}`).join("; "),
      expected: expected.map((item) => `${item.label}: ${item.answer}`).join("; "),
      progress: `${count}/${expected.length} correct`,
      resolved: solved,
      attempt: nextAttempt,
    });
  };
  const fieldClass = (index: number) =>
    `min-h-11 rounded-lg border px-3 py-2 text-base focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FD954F] ${checked[index] === false ? "border-[#EF4444] bg-[#EF4444]/10" : checked[index] ? "border-[#22C55E] bg-[#22C55E]/10" : "border-[#3A3A3C] bg-[#1B1C22]"}`;
  return (
    <div className="space-y-5">
      {exercise.family === "conjugation" && (
        <div className="overflow-hidden rounded-lg border border-[#3A3A3C]">
          <table className="w-full table-fixed text-left">
            <thead className="bg-white/[0.03] text-sm text-[#AEAEB2]">
              <tr>
                <th className="w-2/5 px-3 py-3 font-medium">Subject</th>
                <th className="px-3 py-3 font-medium">Verb</th>
              </tr>
            </thead>
            <tbody>
              {exercise.rows.map((row, index) => (
                <tr key={row.subject} className="border-t border-[#3A3A3C]">
                  <th scope="row" className="px-3 py-3 text-base font-medium">
                    {row.subject}
                  </th>
                  <td className="px-3 py-3 text-lg">
                    {row.blank ? (
                      <input
                        type="text"
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        aria-label={`Verb for ${row.subject}`}
                        aria-invalid={checked[index] === false}
                        disabled={checked[index] === true}
                        className={`${fieldClass(index)} w-full min-w-0`}
                        value={answers[index] || ""}
                        onChange={(event) => choose(index, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            check();
                          }
                        }}
                      />
                    ) : (
                      <StudyText text={row.form} focus={[row.emphasis, ...focus]} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {exercise.family === "gap_fill" && (
        <div className="space-y-6">
          {exercise.items.map((item, index) => (
            <fieldset key={index} className="space-y-3">
              <legend className="text-lg leading-relaxed">
                <span className="mr-3 text-sm text-[#AEAEB2]">{index + 1}</span>
                <StudyText text={item.sentence.split(/_{3,}/)[0]} focus={focus} />
                <span
                  className={`mx-1 inline-block min-w-16 border-b-2 px-2 text-center ${checked[index] === false ? "border-[#EF4444]" : "border-[#FD954F]"}`}
                >
                  {answers[index] || "…"}
                </span>
                <StudyText text={item.sentence.split(/_{3,}/)[1]} focus={focus} />
              </legend>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label={`Options for sentence ${index + 1}`}
              >
                {item.options.map((option) => (
                  <button
                    type="button"
                    key={option}
                    disabled={checked[index] === true}
                    aria-pressed={answers[index] === option}
                    className={`${fieldClass(index)} ${answers[index] === option ? "ring-2 ring-[#FD954F]" : ""}`}
                    onClick={() => choose(index, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {checked[index] === false && (
                <p className="text-sm text-[#FCA5A5]">Try this sentence again.</p>
              )}
            </fieldset>
          ))}
        </div>
      )}
      {exercise.family === "word_tiles" && (
        <>
          <p className="text-lg font-medium leading-relaxed">
            <StudyText text={exercise.prompt} focus={focus} />
          </p>
          <div
            aria-label="Your sentence"
            className="flex min-h-24 flex-wrap content-start gap-2 rounded-lg border border-dashed border-[#3A3A3C] bg-white/[0.02] p-3"
          >
            {order.length === 0 && (
              <span className="self-center text-sm text-[#AEAEB2]">Tap the tiles in order.</span>
            )}
            {order.map((index, position) => (
              <button
                type="button"
                key={index}
                disabled={done}
                aria-label={`Remove ${tiles[index]}`}
                className={studySecondary}
                onClick={() => {
                  setOrder((prev) => prev.filter((_, i) => i !== position));
                  setStatus("");
                }}
              >
                {tiles[index]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Available tiles">
            {tiles.map((tile, index) => (
              <button
                type="button"
                key={index}
                disabled={done || order.includes(index)}
                className={studySecondary}
                onClick={() => {
                  setOrder((prev) => [...prev, index]);
                  setStatus("");
                }}
              >
                {tile}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={done || order.length === 0}
            className={studySecondary}
            onClick={() => {
              setOrder([]);
              setStatus("");
            }}
          >
            Start again
          </button>
        </>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="flex-1 text-sm leading-relaxed text-[#D1D1D6]">
          {status}
        </p>
        <button type="button" className={studyPrimary} disabled={!canCheck} onClick={check}>
          {done ? "Checked" : "Check"}
        </button>
      </div>
    </div>
  );
}
